<template>
    <div v-if="state.loading.availableVendors" :class="embedded ? 'data-form-loading' : 'data-card data-form-loading'">
        <ProgressSpinner />
    </div>

    <div v-else :class="embedded ? '' : 'data-card data-form'">

        <div v-if="!embedded" class="data-card-toolbar">
            <span class="record-name">{{ title }}</span>
        </div>

        <form class="data-form-grid data-form-grid-3" @submit.prevent="checkInvalidValuesAndCallApi">

            <span class="data-form-section-title">Configurações gerais</span>

            <div class="form-field data-form-full">
                <label for="config-vendors">Fornecedores</label>
                <MultiSelect inputId="config-vendors" display="chip" filter v-model="state.selectedVendors"
                    :options="state.availableVendors" optionLabel="name" optionDisabled="occupied"
                    :maxSelectedLabels="6" placeholder="Selecione os fornecedores">
                    <template #option="slotProps">
                        <div class="vendor-option">
                            <span>{{ slotProps.option.name }}</span>
                            <span v-if="slotProps.option.occupied" class="vendor-option-owner">
                                em uso por {{ slotProps.option.occupied_by_client_name }}
                            </span>
                        </div>
                    </template>
                </MultiSelect>

                <small v-if="hasOccupiedVendor" class="form-hint">
                    Fornecedor Metagrator pertence a um cliente só, de forma definitiva. Para este cliente,
                    cadastre uma conta Metagrator própria.
                </small>
            </div>

            <div class="form-field">
                <label for="config-deposit-status">Status de depósito</label>
                <Dropdown inputId="config-deposit-status" v-model="state.deposit_enabled"
                    :options="state.enabledOptions" optionLabel="text" />
            </div>

            <div class="form-field">
                <label for="config-withdraw-status">Status de saque</label>
                <Dropdown inputId="config-withdraw-status" v-model="state.withdraw_enabled"
                    :options="state.enabledOptions" optionLabel="text" />
            </div>

            <span class="data-form-section-title is-deposit">
                <i class="pi pi-arrow-down"></i>
                Depósito
            </span>

            <div class="form-field">
                <label for="config-min-deposit">Valor mínimo por transação</label>
                <InputNumber inputId="config-min-deposit" v-model="state.configuration.min_deposit_value"
                    mode="currency" currency="BRL" />
            </div>

            <div class="form-field">
                <label for="config-max-deposit">Valor máximo por transação</label>
                <InputNumber inputId="config-max-deposit" v-model="state.configuration.max_deposit_value"
                    mode="currency" currency="BRL" />
            </div>

            <div class="form-field">
                <label for="config-daily-deposit">Limite diário</label>
                <InputNumber inputId="config-daily-deposit" v-model="state.configuration.daily_deposit_amount"
                    mode="currency" currency="BRL" />
            </div>

            <div class="form-field">
                <label for="config-number-deposit">Quantidade por dia</label>
                <InputNumber inputId="config-number-deposit" v-model="state.configuration.daily_number_deposit" />
            </div>

            <div class="form-field">
                <label for="config-daily-deposit-cpf">Limite diário por CPF</label>
                <InputNumber inputId="config-daily-deposit-cpf"
                    v-model="state.configuration.daily_deposit_amount_per_cpf" mode="currency" currency="BRL" />
            </div>

            <div class="form-field">
                <label for="config-number-deposit-cpf">Quantidade diária por CPF</label>
                <InputNumber inputId="config-number-deposit-cpf"
                    v-model="state.configuration.daily_number_deposit_per_cpf" />
            </div>

            <span class="data-form-section-title is-withdraw">
                <i class="pi pi-arrow-up"></i>
                Saque
            </span>

            <div class="form-field">
                <label for="config-min-withdraw">Valor mínimo por transação</label>
                <InputNumber inputId="config-min-withdraw" v-model="state.configuration.min_withdraw_value"
                    mode="currency" currency="BRL" />
            </div>

            <div class="form-field">
                <label for="config-max-withdraw">Valor máximo por transação</label>
                <InputNumber inputId="config-max-withdraw" v-model="state.configuration.max_withdraw_value"
                    mode="currency" currency="BRL" />
            </div>

            <div class="form-field">
                <label for="config-daily-withdraw">Limite diário</label>
                <InputNumber inputId="config-daily-withdraw" v-model="state.configuration.daily_withdraw_amount"
                    mode="currency" currency="BRL" />
            </div>

            <div class="form-field">
                <label for="config-number-withdraw">Quantidade por dia</label>
                <InputNumber inputId="config-number-withdraw" v-model="state.configuration.daily_number_withdraw" />
            </div>

            <div class="form-field">
                <label for="config-daily-withdraw-cpf">Limite diário por CPF</label>
                <InputNumber inputId="config-daily-withdraw-cpf"
                    v-model="state.configuration.daily_withdraw_amount_per_cpf" mode="currency" currency="BRL" />
            </div>

            <div class="form-field">
                <label for="config-number-withdraw-cpf">Quantidade diária por CPF</label>
                <InputNumber inputId="config-number-withdraw-cpf"
                    v-model="state.configuration.daily_number_withdraw_per_cpf" />
            </div>

            <div class="data-form-footer">
                <Button v-if="embedded" class="p-button-text" type="button" label="Cancelar"
                    @click="$emit('closeUpdateModal')" />
                <Button :loading="state.tryingToCreateConfiguration || state.tryingToUpdateConfiguration" type="submit"
                    :label="buttonLabel" icon="pi pi-check" />
            </div>

        </form>
    </div>
</template>

<script>

import InputNumber from 'primevue/inputnumber'
import MultiSelect from 'primevue/multiselect'
import Dropdown from 'primevue/dropdown'
import Button from 'primevue/button'
import ProgressSpinner from 'primevue/progressspinner'

import { reactive, computed } from 'vue'
import useNotificationToast from '../../../composables/useNotificationToast'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import services from '../../../services'

export default {

    inheritAttrs: false,

    components: { InputNumber, Dropdown, MultiSelect, Button, ProgressSpinner },

    props: {
        title: {
            type: String,
            default: 'Criando Nova Configuração'
        },
        client: {
            type: Object
        },
        configurationToUpate: {
            type: Object
        },
        isUpdanting: {
            type: Boolean,
            deafult: false
        },
        isCloning: {
            type: Boolean,
            deafult: false
        },
        // dentro de um modal o formulário não desenha o próprio card
        embedded: {
            type: Boolean,
            default: false
        },
    },

    emits: ['realoadConfigurations', 'closeUpdateModal'],

    setup(props, context) {

        const enabledOptions = [
            { value: true, text: 'Ativado' },
            { value: false, text: 'Desativado' }
        ]

        const state = reactive({
            availableVendors: null,
            selectedVendors: [],
            configuration: {
                min_withdraw_value: null,
                max_withdraw_value: null,
                min_deposit_value: null,
                max_deposit_value: null,
                daily_withdraw_amount: null,
                daily_deposit_amount: null,
                daily_number_deposit: null,
                daily_number_withdraw: null,
                daily_deposit_amount_per_cpf: null,
                daily_withdraw_amount_per_cpf: null,
                daily_number_withdraw_per_cpf: null,
                daily_number_deposit_per_cpf: null,
                withdraw_enabled: { value: true, text: 'Ativado' },
                deposit_enabled: { value: true, text: 'Ativado' },
                manual_withdraw: { value: true, text: 'Ativado' },
            },
            // os dropdowns já nascem preenchidos: o serviço lê .value ao enviar
            withdraw_enabled: enabledOptions[0],
            deposit_enabled: enabledOptions[0],
            manual_withdraw: { value: false, text: 'Desativado' },
            enabledOptions,
            loading: {
                availableVendors: false
            },
            tryingToCreateConfiguration: false,
            tryingToUpdateConfiguration: false
        })

        if (props.configurationToUpate) {

            state.loading.availableVendors = true

            Object.assign(state.configuration, props.configurationToUpate)

            state.selectedVendors = state.configuration.vendors
            state.withdraw_enabled = { value: state.configuration.withdraw_enabled, text: changeBooleanToText(state.configuration.withdraw_enabled) }
            state.deposit_enabled = { value: state.configuration.deposit_enabled, text: changeBooleanToText(state.configuration.deposit_enabled) }
            state.manual_withdraw = { value: state.configuration.manual_withdraw, text: changeBooleanToText(state.configuration.manual_withdraw) }

        }


        if (props.isCloning) {
            state.selectedVendors = []
            state.configuration.vendors = []
        }

        const { success, error } = useNotificationToast()
        const store = useStore()
        const route = useRoute()
        const router = useRouter()

        loadAvailableVendors()

        function loadAvailableVendors() {
            state.loading.availableVendors = true

            const token = store.getters.getToken
            const clientId = route.params.id

            services.configurations.getAvailableVendors({ clientId, token })
                .then((data) => {
                    const availableVendors = data.data
                    state.availableVendors = state.selectedVendors.concat(availableVendors)
                })
                .catch(() => {
                    error("Não foi possível carregar os fornecedores.")
                })
                .finally(() => { state.loading.availableVendors = false })
        }

        const hasOccupiedVendor = computed(() =>
            (state.availableVendors || []).some((vendor) => vendor.occupied)
        )

        function changeBooleanToText(value) {
            return value ? 'Ativado' : 'Desativado'
        }

        const hasEmptyValues = computed(() => {

            let hasNullValue = false

            for (const key in state.configuration) {

                if (key !== 'priority' && state.configuration[key] === null) {
                    hasNullValue = true
                    break
                }

            }

            return hasNullValue
        })

        const buttonLabel = computed(() => {
            const action = props.isUpdanting ? 'Atualizar' : 'Criar'
            return `${action} configuração`
        })

        function checkInvalidValuesAndCallApi() {
            if (hasEmptyValues.value) {
                return error('Verifique todos os campos!')
            }

            if (checkIfHasInvalidValues()) {
                return error('Valores inválidos!')
            }

            props.isUpdanting ? tryToUpdateConfiguration() : tryToCreateConfiguration()
        }

        function checkIfHasInvalidValues() {

            let hasInvalidValues = false

            Object.keys(state.configuration).forEach(item => {


                if (item != 'manual_withdraw' && item != 'priority') {

                    if (item != 'id' && item != 'client_id' && item != 'created_at' && item != 'updated_at' && item != 'vendor' && typeof state.configuration[item] === 'number' && state.configuration[item] < 0.01) {
                        hasInvalidValues = true

                    }
                }

            })

            return hasInvalidValues

        }

        function getIdsFromSelectedVendors() {
            const selectedVendorsId = []

            state.selectedVendors.forEach(vendor => {
                selectedVendorsId.push(vendor.id)
            })

            return selectedVendorsId
        }

        async function tryToUpdateConfiguration() {

            state.tryingToUpdateConfiguration = true

            const token = store.getters.getToken

            state.configuration.withdraw_enabled = state.withdraw_enabled
            state.configuration.deposit_enabled = state.deposit_enabled
            state.configuration.manual_withdraw = state.manual_withdraw

            const vendorsId = getIdsFromSelectedVendors()

            await services.configurations.update({
                vendorsId,
                configuration: state.configuration,
                token
            }).then(() => {

                success('Configuração atualizada com sucesso!')

                context.emit('closeUpdateModal')

            }).catch((e) => {

                error(getErrorMessage(e, 'Não foi possível atualizar a configuração.'))

            }).finally(() => { state.tryingToUpdateConfiguration = false })

        }

        async function tryToCreateConfiguration() {

            state.tryingToCreateConfiguration = true

            const token = store.getters.getToken

            state.configuration.withdraw_enabled = state.withdraw_enabled
            state.configuration.deposit_enabled = state.deposit_enabled
            state.configuration.manual_withdraw = state.manual_withdraw

            const vendorsId = getIdsFromSelectedVendors()

            await services.configurations.create({
                clientId: props.client.id,
                vendorsId,
                configuration: state.configuration,
                token
            }).then(() => {

                state.selectedVendors = []
                state.configuration = {
                    min_withdraw_value: null,
                    max_withdraw_value: null,
                    min_deposit_value: null,
                    max_deposit_value: null,
                    daily_withdraw_amount: null,
                    daily_deposit_amount: null,
                    daily_number_deposit: null,
                    daily_number_withdraw: null,
                    daily_deposit_amount_per_cpf: null,
                    daily_withdraw_amount_per_cpf: null,
                    daily_number_withdraw_per_cpf: null,
                    daily_number_deposit_per_cpf: null,
                }
                state.withdraw_enabled = enabledOptions[0]
                state.deposit_enabled = enabledOptions[0]

                if (props.isCloning) {

                    success('Configuração criada com sucesso!')

                    context.emit('closeUpdateModal')

                } else {

                    router.push({ name: 'client-configurations', params: { id: props.client.id } }).then(() => {
                        context.emit('realoadConfigurations', { successMessage: 'Configuração criada com sucesso!', tabMenuIndex: 0 })
                    })

                }

            }).catch((e) => {

                error(getErrorMessage(e, 'Não foi possível registrar o fornecedor.'))

            }).finally(() => { state.tryingToCreateConfiguration = false })

        }

        // a API devolve a mensagem em minúsculo; o toast exibe com a inicial maiúscula
        function getErrorMessage(exception, fallback) {

            const message = exception && exception.response && exception.response.data && exception.response.data.message

            if (!message) return fallback

            return message.charAt(0).toUpperCase() + message.slice(1)

        }

        return {
            state,
            changeBooleanToText,
            checkInvalidValuesAndCallApi,
            buttonLabel,
            hasOccupiedVendor
        }

    }
}
</script>

<style scoped>
.vendor-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
}

.vendor-option-owner {
    margin-left: auto;
    font-size: 0.75rem;
    opacity: 0.7;
}
</style>
